import React from 'react';
import { Modal, Tabs } from 'antd';
import { FullscreenOutlined } from '@ant-design/icons';
interface Props {
  open: boolean;
  onClose: () => void;
}

// ── Shared styles ─────────────────────────────────────────────────────────────
const S = {
  h2: { margin: '0 0 10px', fontSize: 14, fontWeight: 700, color: '#e2e8f0', letterSpacing: 0.3 } as React.CSSProperties,
  h3: { margin: '16px 0 6px', fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' as const, letterSpacing: 1 },
  p:  { margin: '0 0 10px', color: '#94a3b8', fontSize: 12, lineHeight: 1.8 },
  ul: { margin: '0 0 10px', paddingLeft: 18, color: '#94a3b8', fontSize: 12, lineHeight: 1.8 },
  accent: (color: string): React.CSSProperties => ({
    display: 'inline-block', padding: '0 7px', borderRadius: 3,
    fontSize: 11, fontWeight: 600, lineHeight: '18px',
    background: color + '22', border: `1px solid ${color}88`, color,
  }),
  kbd: {
    display: 'inline-block', padding: '0 5px', borderRadius: 3,
    border: '1px solid #555', background: '#2a2a2a', color: '#ddd',
    fontSize: 11, lineHeight: '18px', fontFamily: 'monospace',
  } as React.CSSProperties,
  tip: {
    background: 'rgba(88,166,255,0.07)', border: '1px solid rgba(88,166,255,0.2)',
    borderRadius: 6, padding: '8px 12px', marginTop: 8, marginBottom: 10,
    color: '#7dd3fc', fontSize: 11, lineHeight: 1.7,
  } as React.CSSProperties,
  grid: {
    display: 'grid', gridTemplateColumns: '140px 1fr', gap: '4px 12px',
    marginBottom: 10,
  } as React.CSSProperties,
  label: { color: '#64748b', fontSize: 11 } as React.CSSProperties,
  value: { color: '#94a3b8', fontSize: 12, lineHeight: 1.7 } as React.CSSProperties,
};

const Kb: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <kbd style={S.kbd}>{children}</kbd>
);

// Platform-aware modifier key labels
const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/i.test(navigator.platform);
const CTRL = isMac ? 'Cmd' : 'Ctrl';
const OPT  = isMac ? 'Option' : 'Alt';

// ── Sections ──────────────────────────────────────────────────────────────────

const Overview: React.FC = () => (
  <div>
    <h2 style={S.h2}>Welcome to StoryBall</h2>
    <p style={S.p}>
      StoryBall is a browser-based 3D storyboarding and animation tool. You paint directly
      onto the surface of a sphere — ideal for 360° panoramas, dome films, and spatial
      narrative layouts. Layers stack like Photoshop, and a keyframe timeline brings
      your compositions to life over time.
    </p>

    <h3 style={S.h3}>Interface at a glance</h3>
    <div style={S.grid}>
      <span style={S.label}>Menu bar</span>
      <span style={S.value}>File · Edit · View · Help — access all major commands.</span>
      <span style={S.label}>Main toolbar</span>
      <span style={S.value}>Tool selector (left), Dup mode (centre), Texture resolution (right).</span>
      <span style={S.label}>Context strip</span>
      <span style={S.value}>A second row that changes with the active tool — brush size, color, projection, etc.</span>
      <span style={S.label}>Viewport</span>
      <span style={S.value}>The 3D sphere. Paint here. Orbit / zoom / pan the camera around it.</span>
      <span style={S.label}>Right panel</span>
      <span style={S.value}>UV map preview + Layers list. Toggle with the ◂ / ▸ button on the right edge.</span>
      <span style={S.label}>Timeline</span>
      <span style={S.value}>Keyframe editor at the bottom. Toggle with ▴ / ▾ button at the bottom edge.</span>
    </div>

    <div style={S.tip}>
      💡 Panels can be hidden to give the viewport more room. The toggle buttons float at the
      viewport edges — look for the ◂ and ▴ arrows.
    </div>

    <h3 style={S.h3}>Quick start</h3>
    <ol style={{ ...S.ul, paddingLeft: 22 }}>
      <li>Press <Kb>Q</Kb> to select the Draw tool, or click <strong>Draw</strong> in the toolbar.</li>
      <li>Pick a color in the context strip below the toolbar.</li>
      <li>Click and drag on the sphere to paint.</li>
      <li>Add a new layer in the Layers panel to keep strokes separate.</li>
      <li>Open the Timeline to add keyframes and animate layer position and opacity.</li>
      <li>Save with <Kb>{CTRL}</Kb> + <Kb>S</Kb>.</li>
    </ol>
  </div>
);

const ToolsGuide: React.FC = () => (
  <div>
    <h2 style={S.h2}>Tools</h2>

    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={S.accent('#58a6ff')}>Draw</span>
        <span style={{ ...S.label }}><Kb>Q</Kb></span>
      </div>
      <p style={S.p}>
        Freehand brush that paints onto the selected layer. Supports two projection modes:
      </p>
      <div style={S.grid}>
        <span style={S.label}>Wrap</span>
        <span style={S.value}>Strokes follow UV space — they stay fixed to the sphere surface as you rotate.</span>
        <span style={S.label}>Project</span>
        <span style={S.value}>Strokes are projected from the camera — like spray-painting a wall.</span>
      </div>
      <p style={S.p}>
        Use the <strong>Size</strong>, <strong>Opacity</strong>, and <strong>Smooth</strong> sliders in the context strip.
        Enable <strong>Pressure</strong> if you have a stylus.
      </p>
      <div style={S.tip}>
        💡 Hold <Kb>V</Kb> and drag left/right to scrub opacity quickly. Hold <Kb>B</Kb> to scrub brush size.
      </div>
    </div>

    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={S.accent('#4ade80')}>Fill</span>
        <span style={S.label}><Kb>W</Kb></span>
      </div>
      <p style={S.p}>
        Click to flood-fill from the click point using the current color or gradient. Drag to define a gradient axis. Right-click to fill using color 2.
      </p>
      <div style={S.grid}>
        <span style={S.label}>Island</span>
        <span style={S.value}>Fill only the contiguous region of similar color connected to the click point.</span>
        <span style={S.label}>Non-Contiguous</span>
        <span style={S.value}>Fill all pixels of similar color across the entire layer, even if not connected.</span>
        <span style={S.label}>Global</span>
        <span style={S.value}>Fill the entire layer regardless of existing color. Tolerance is ignored.</span>
        <span style={S.label}>Tol slider</span>
        <span style={S.value}>How similar a pixel's color must be to the seed color to be filled (0 = exact match, 100 = fill everything). Hold <Kb>B</Kb> + drag to scrub live.</span>
      </div>
    </div>

    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={S.accent('#f87171')}>Erase</span>
        <span style={S.label}><Kb>E</Kb></span>
      </div>
      <p style={S.p}>
        Works like Draw but removes paint instead of adding it. All the same projection
        modes, size, and opacity controls apply.
      </p>
    </div>

    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={S.accent('#f5a623')}>Stamp</span>
        <span style={S.label}><Kb>R</Kb></span>
      </div>
      <p style={S.p}>
        Projects an image onto the sphere with a single click. Click <strong>Pick stamp</strong> to
        choose an image, then click on the sphere to place it.
      </p>
      <div style={S.grid}>
        <span style={S.label}>Projection</span>
        <span style={S.value}>Wrap or Project — same as Draw.</span>
        <span style={S.label}>Flip X / Flip Y</span>
        <span style={S.value}>Mirror the stamp image horizontally or vertically.</span>
        <span style={S.label}>Size slider</span>
        <span style={S.value}>Scale the stamp. Hold <Kb>B</Kb> + drag to scrub live.</span>
        <span style={S.label}>Rotation slider</span>
        <span style={S.value}>Rotate the stamp −180° to +180°. Hold <Kb>C</Kb> + drag to scrub live.</span>
      </div>
    </div>

    <div style={{ marginBottom: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={S.accent('#a78bfa')}>Text</span>
        <span style={S.label}><Kb>T</Kb></span>
      </div>
      <p style={S.p}>
        Renders text onto the sphere. Type in the text field, then click on the sphere to place it.
      </p>
      <div style={S.grid}>
        <span style={S.label}>Font</span>
        <span style={S.value}>Select from the available system fonts.</span>
        <span style={S.label}>B / I</span>
        <span style={S.value}>Bold and Italic toggles.</span>
        <span style={S.label}>⬅ ↔ ➡</span>
        <span style={S.value}>Left / Centre / Right alignment.</span>
        <span style={S.label}>Kerning</span>
        <span style={S.value}>Letter spacing in em units.</span>
        <span style={S.label}>Line H</span>
        <span style={S.value}>Line height multiplier.</span>
        <span style={S.label}>Text box</span>
        <span style={S.value}>Multi-line text entry. Press Enter for new lines.</span>
      </div>
    </div>

    <div style={{ marginBottom: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={S.accent('#22d3ee')}>Blur</span>
        <span style={S.label}><Kb>Y</Kb></span>
      </div>
      <p style={S.p}>
        Softens paint on the selected layer. Drag across any area to progressively blur it.
        Each stroke pass blends neighbouring pixels together, so multiple passes build up
        stronger softening.
      </p>
      <div style={S.grid}>
        <span style={S.label}>Size</span>
        <span style={S.value}>Brush footprint on the sphere — same as Draw and Erase.</span>
        <span style={S.label}>Radius</span>
        <span style={S.value}>Blur kernel strength. Higher values produce a wider, softer spread per pixel. Hold <Kb>C</Kb> + drag left/right to scrub live — the UV panel's inner reticle ring tracks the current radius.</span>
        <span style={S.label}>Opacity</span>
        <span style={S.value}>How much each stroke blends toward the blurred result (lower = gentler passes).</span>
      </div>
      <div style={S.tip}>
        💡 Use a low opacity and multiple passes for a natural, graduated softening effect.
      </div>
    </div>
  </div>
);

const CameraGuide: React.FC = () => (
  <div>
    <h2 style={S.h2}>Camera Controls</h2>
    <p style={S.p}>
      The viewport camera orbits around the sphere. You always look at the centre;
      your position on an imaginary shell around the sphere is what changes.
    </p>

    <h3 style={S.h3}>Mouse</h3>
    <div style={S.grid}>
      <span style={S.label}>{OPT} + Left drag</span>
      <span style={S.value}>Orbit — rotate the camera around the sphere.</span>
      <span style={S.label}>Scroll wheel</span>
      <span style={S.value}>Zoom in / out.</span>
      <span style={S.label}>Right drag</span>
      <span style={S.value}>Pan — shift the camera laterally.</span>
      <span style={S.label}><Kb>F</Kb></span>
      <span style={S.value}>Re-centre the camera to the default front view.</span>
      <span style={S.label}><Kb>←</Kb> / <Kb>→</Kb></span>
      <span style={S.value}>Step the camera 10° around the Y axis.</span>
    </div>

    <h3 style={S.h3}>Predefined Camera Positions</h3>
    <p style={S.p}>
      Press keys <Kb>1</Kb> – <Kb>9</Kb> and <Kb>0</Kb> to snap to predefined camera angles
      (front, back, sides, top, etc.). These are also accessible under <strong>View → Predefined Cameras</strong>.
    </p>
    <div style={S.tip}>
      💡 Enable <strong>Hero Camera Snap</strong> (View menu) to make the camera
      automatically snap to the nearest predefined position when you release the mouse.
    </div>

    <h3 style={S.h3}>Tablet Mode Camera</h3>
    <p style={S.p}>
      When Tablet Mode is on (View → Tablet Mode), a row of camera buttons appears inside
      the camera panel: <strong>⤾ Orbit</strong>, <strong>⊕ Zoom</strong>, and <strong>✛ Pan</strong>.
      Select one to change what your finger does on the background of the sphere.
    </p>
  </div>
);

const LayersGuide: React.FC = () => (
  <div>
    <h2 style={S.h2}>Layers</h2>
    <p style={S.p}>
      The Layers panel (right side of the screen) works like a standard digital painting
      application. Each layer holds its own painted content, opacity, and transform, and
      can be animated independently on the timeline.
    </p>

    <h3 style={S.h3}>Creating & organising layers</h3>
    <div style={S.grid}>
      <span style={S.label}>+ button</span>
      <span style={S.value}>Add a new blank layer above the currently selected one.</span>
      <span style={S.label}>Click a layer</span>
      <span style={S.value}>Select it — all painting goes to the selected layer.</span>
      <span style={S.label}>Eye icon</span>
      <span style={S.value}>Show / hide the layer in the viewport.</span>
      <span style={S.label}>Opacity slider</span>
      <span style={S.value}>Set the layer's global opacity (0 – 100%). Hold <Kb>X</Kb> + drag in the viewport to scrub.</span>
      <span style={S.label}><Kb>F2</Kb></span>
      <span style={S.value}>Rename the selected layer.</span>
      <span style={S.label}><Kb>{CTRL}</Kb> + <Kb>↑</Kb> / <Kb>↓</Kb></span>
      <span style={S.value}>Move layer up / down in the stack.</span>
      <span style={S.label}><Kb>{CTRL}</Kb> + <Kb>D</Kb></span>
      <span style={S.value}>Duplicate the selected layer (copies texture and keyframes).</span>
      <span style={S.label}><Kb>Backspace</Kb></span>
      <span style={S.value}>Delete the selected layer (prompts for confirmation if the layer has content).</span>
      <span style={S.label}><Kb>{OPT}</Kb> + <Kb>Backspace</Kb></span>
      <span style={S.value}>Clear the selected layer — erases all painted content, leaving it fully transparent.</span>
      <span style={S.label}><Kb>Clear button</Kb></span>
      <span style={S.value}>Same as <Kb>{OPT}</Kb> + <Kb>Backspace</Kb> — the eraser icon at the top of the Layers panel.</span>
      <span style={S.label}>Lock icon</span>
      <span style={S.value}>Lock or unlock the layer. Locked layers cannot be painted on or transformed. A layer is also locked if any ancestor is locked (shown with a faded amber icon).</span>
      <span style={S.label}>Merge button</span>
      <span style={S.value}>Merge the selected layer's content down onto the layer immediately below it. The selected layer is removed afterward.</span>
    </div>

    <h3 style={S.h3}>Layer hierarchy</h3>
    <p style={S.p}>
      Layers can be nested into a parent/child tree. A child layer inherits its parent's
      transform and visibility.
    </p>
    <div style={S.grid}>
      <span style={S.label}>Drag layer handle</span>
      <span style={S.value}>Reorder layers by dragging the grip icon. Drop onto the middle zone of another layer to make it a child (indented under the parent).</span>
      <span style={S.label}>Outdent button</span>
      <span style={S.value}>Visible on child layers — click to remove the parent relationship and promote the layer back to the root level.</span>
    </div>

    <h3 style={S.h3}>Layer transform</h3>
    <p style={S.p}>
      Each layer can be rotated and translated independently of others — useful for
      blocking out elements at different positions on the sphere.
    </p>
    <div style={S.grid}>
      <span style={S.label}>Shift + Drag</span>
      <span style={S.value}>Rotate the selected layer around the X and Y sphere axes.</span>
      <span style={S.label}>Shift + {OPT} + Drag</span>
      <span style={S.value}>Translate the selected layer (offset from the sphere surface).</span>
    </div>
    <div style={S.tip}>
      💡 If Auto-Key is enabled, transforms will automatically create keyframes as you
      drag — enabling smooth animated layer movement.
    </div>

    <h3 style={S.h3}>Numeric inputs</h3>
    <p style={S.p}>
      All numeric fields in the Layers panel (opacity, rotation X/Y/Z, translate X/Y)
      and the timeline controls (FPS, duration) support two extra interaction modes
      beyond direct typing:
    </p>
    <div style={S.grid}>
      <span style={S.label}>Scroll wheel</span>
      <span style={S.value}>Hover over any numeric input and scroll up/down to step through values one increment at a time.</span>
      <span style={S.label}>Middle-drag</span>
      <span style={S.value}>Hold the middle mouse button on a numeric input and drag up to increase or down to decrease the value continuously.</span>
    </div>

    <h3 style={S.h3}>Dup mode cursor cue</h3>
    <p style={S.p}>
      When painting, colored dots appear above the brush cursor to show how the active
      layer relates to the current global dup mode:
    </p>
    <div style={S.grid}>
      <span style={S.label}>White ●</span>
      <span style={S.value}>The layer is in Single mode — paint affects only the primary copy regardless of the global setting.</span>
      <span style={S.label}>Green ●●…</span>
      <span style={S.value}>The layer’s dup mode matches the global mode — the stroke replicates correctly across all copies.</span>
      <span style={S.label}>Red ●●…</span>
      <span style={S.value}>Mismatch — the layer’s own dup mode differs from the global setting. Change one to align them.</span>
    </div>
    <p style={S.p}>
      The number of dots also indicates the current global dup count: 1 for Single, 2 for
      Diptych, 3 for Triptych, 4 for Quadriptych.
    </p>
  </div>
);

const TimelineGuide: React.FC = () => (
  <div>
    <h2 style={S.h2}>Timeline &amp; Animation</h2>
    <p style={S.p}>
      StoryBall uses a traditional keyframe timeline. Toggle it with the ▴ / ▾ button
      at the bottom of the viewport, or resize it by dragging the divider.
    </p>

    <h3 style={S.h3}>Playback</h3>
    <div style={S.grid}>
      <span style={S.label}><Kb>Space</Kb></span>
      <span style={S.value}>Play / Pause.</span>
      <span style={S.label}>Click ruler</span>
      <span style={S.value}>Jump playhead to that frame.</span>
      <span style={S.label}>In / Out points</span>
      <span style={S.value}>Set with <Kb>I</Kb> / <Kb>O</Kb> to loop a range. Clear with <Kb>Shift</Kb>+<Kb>I</Kb> / <Kb>Shift</Kb>+<Kb>O</Kb>.</span>
    </div>

    <h3 style={S.h3}>Layer timing bars</h3>
    <p style={S.p}>
      Every layer has a colored bar in the timeline that represents the span of frames
      during which it is visible. Layers are invisible before the bar starts and after it
      ends, regardless of keyframes.
    </p>
    <div style={S.grid}>
      <span style={S.label}>Drag bar body</span>
      <span style={S.value}>Slide the layer's active window in time. Any keyframes on that layer move with it.</span>
      <span style={S.label}>Drag left edge</span>
      <span style={S.value}>Trim the start frame of the layer's visible window.</span>
      <span style={S.label}>Drag right edge</span>
      <span style={S.value}>Trim the end frame of the layer's visible window.</span>
    </div>
    <div style={S.tip}>
      💡 Layers with their eye icon turned off show a diagonal stripe pattern on their bar to indicate they are globally hidden.
    </div>

    <h3 style={S.h3}>Keyframes</h3>
    <div style={S.grid}>
      <span style={S.label}><Kb>S</Kb></span>
      <span style={S.value}>Set a keyframe on all channels of the selected layer at the current frame.</span>
      <span style={S.label}>Double-click track</span>
      <span style={S.value}>Add a keyframe at the current playhead position.</span>
      <span style={S.label}>Double-click diamond</span>
      <span style={S.value}>Remove that keyframe.</span>
      <span style={S.label}>Drag diamond</span>
      <span style={S.value}>Move a keyframe to a different frame.</span>
      <span style={S.label}>Drag empty area</span>
      <span style={S.value}>Region-select multiple keyframes.</span>
      <span style={S.label}><Kb>{CTRL}</Kb>+<Kb>C</Kb> / <Kb>V</Kb></span>
      <span style={S.value}>Copy / paste selected keyframes at the playhead.</span>
      <span style={S.label}><Kb>Delete</Kb> / <Kb>Backspace</Kb></span>
      <span style={S.value}>Delete selected keyframes.</span>
    </div>

    <h3 style={S.h3}>Auto-Key</h3>
    <p style={S.p}>
      The <strong>AK</strong> and <strong>AK+</strong> buttons in the timeline controls bar enable automatic keyframing when you change layer properties:
    </p>
    <div style={S.grid}>
      <span style={S.label}>AK (off)</span>
      <span style={S.value}>No automatic keyframing — changes to opacity, rotation, and translation are applied immediately with no keyframe created.</span>
      <span style={S.label}>AK</span>
      <span style={S.value}>A keyframe is automatically created on a channel only if that channel already has at least one existing keyframe.</span>
      <span style={S.label}>AK+</span>
      <span style={S.value}>A keyframe is always created on any channel you modify, even if no prior keyframes exist on that channel.</span>
    </div>
    <div style={S.tip}>
      💡 In the Layers panel, hold <Kb>Shift</Kb> while adjusting a rotation field to force a keyframe on that channel regardless of the current Auto-Key mode.
    </div>

    <h3 style={S.h3}>Easing</h3>
    <p style={S.p}>
      Right-click a keyframe diamond to open the easing menu and choose interpolation
      — Linear, Ease In, Ease Out, Ease In/Out, or Step.
    </p>

    <h3 style={S.h3}>Camera track</h3>
    <p style={S.p}>
      The top track in the timeline is the camera. Set keyframes here with <Kb>S</Kb> to
      animate the camera orbiting automatically during playback.
    </p>
  </div>
);

const ProjectGuide: React.FC = () => (
  <div>
    <h2 style={S.h2}>Projects &amp; Export</h2>

    <h3 style={S.h3}>Saving &amp; opening</h3>
    <div style={S.grid}>
      <span style={S.label}><Kb>{CTRL}</Kb>+<Kb>S</Kb></span>
      <span style={S.value}>Save the current project. If unsaved, prompts for a filename.</span>
      <span style={S.label}>File → Save As…</span>
      <span style={S.value}>Save with a new name.</span>
      <span style={S.label}><Kb>{CTRL}</Kb>+<Kb>O</Kb></span>
      <span style={S.value}>Open a <code style={{ color: '#94a3b8' }}>.balls</code> project file from disk.</span>
      <span style={S.label}><Kb>{CTRL}</Kb>+<Kb>{OPT}</Kb>+<Kb>N</Kb></span>
      <span style={S.value}>Start a new blank project (clears all layers and timeline).</span>
    </div>

    <h3 style={S.h3}>Texture resolution</h3>
    <p style={S.p}>
      The resolution buttons in the top-right of the toolbar control the size of the
      paint texture that wraps the sphere. Higher resolutions give more detail but use
      more GPU memory.
    </p>
    <div style={S.tip}>
      ⚠️ Lowering the resolution is permanent for the current project — it discards
      texture detail and cannot be undone. A confirmation dialog will appear.
    </div>

    <h3 style={S.h3}>Render</h3>
    <p style={S.p}>
      Open <strong>File → Render…</strong> to export your animation as an image sequence
      or video. Choose resolution, frame range, and format in the Render dialog.
    </p>

    <h3 style={S.h3}>Dup mode</h3>
    <p style={S.p}>
      The centre buttons in the main toolbar control how the sphere content is
      mirrored/repeated:
    </p>
    <div style={S.grid}>
      <span style={S.label}>Single</span>
      <span style={S.value}>Normal single sphere — no duplication.</span>
      <span style={S.label}>Diptych</span>
      <span style={S.value}>Mirrors content across two halves of a panorama.</span>
      <span style={S.label}>Triptych</span>
      <span style={S.value}>Repeats content across three equal panels.</span>
      <span style={S.label}>Quadriptych</span>
      <span style={S.value}>Four-panel repeat.</span>
    </div>
    <p style={S.p}>
      Each layer also has its own per-layer dup mode (set in the Layers panel). When the
      per-layer mode matches the global mode the brush cursor shows green dots; if they
      differ it shows red dots. See the <strong>Layers → Dup mode cursor cue</strong> section
      for details.
    </p>
  </div>
);

const UVGuide: React.FC = () => (
  <div>
    <h2 style={S.h2}>UV View &amp; Clipboard Paste</h2>

    <h3 style={S.h3}>UV viewport swap</h3>
    <p style={S.p}>
      The UV panel normally lives in the right sidebar as a small equirectangular preview.
      You can promote it to the main viewport for flat 2-D editing:
    </p>
    <div style={S.grid}>
      <span style={S.label}><FullscreenOutlined /> button</span>
      <span style={S.value}>Click the expand icon in the lower-left corner of the UV panel to make the UV map fill the main viewport area.</span>
      <span style={S.label}><FullscreenOutlined /> button</span>
      <span style={S.value}>While the UV view is main, click the icon in the lower-left corner of the 3D thumbnail to swap back.</span>
    </div>
    <p style={S.p}>
      When the UV view is main, all drawing tools (Draw, Erase, Blur, Stamp, Text) paint
      directly on the flat equirectangular texture. The camera orbit and zoom controls are
      inactive; Diptych/Triptych/Quadriptych and Project projection mode are also disabled.
    </p>
    <div style={S.tip}>
      💡 Use UV view for precise flat placement of stamps, text, or reference imagery where
      the spherical perspective distortion of the 3D view makes positioning awkward.
    </div>

    <h3 style={S.h3}>Clipboard paste</h3>
    <p style={S.p}>
      You can paste any image from your system clipboard directly onto the selected layer.
    </p>
    <div style={S.grid}>
      <span style={S.label}><Kb>{CTRL}</Kb>+<Kb>V</Kb></span>
      <span style={S.value}>Read an image from the system clipboard (any app — screenshot, browser, Photoshop, etc.) and open the paste controls.</span>
    </div>
    <p style={S.p}>
      A floating control bar appears at the bottom of the viewport showing a thumbnail of
      the pasted image and a scale slider:
    </p>
    <div style={S.grid}>
      <span style={S.label}>Scale slider</span>
      <span style={S.value}>0.1× – 3.0× of fit. At 100% the image is scaled to fill the layer as large as possible without distortion (letterboxed to the layer bounds).</span>
      <span style={S.label}>Commit to Layer</span>
      <span style={S.value}>Bakes the image centred on the layer at the chosen scale. This creates an undo snapshot so it can be undone with <Kb>{CTRL}</Kb>+<Kb>Z</Kb>.</span>
      <span style={S.label}>Cancel</span>
      <span style={S.value}>Discard the pasted image without modifying any layer.</span>
    </div>
    <p style={S.p}>
      While the paste bar is open, a preview of the image (with a dashed blue border) is
      shown in both the main viewport and the UV panel. Adjust the scale and see the result
      live before committing.
    </p>
    <div style={S.tip}>
      💡 For the most accurate placement, swap to UV view before pasting — the flat map
      makes it easy to see exactly where the image will land on the sphere.
    </div>
  </div>
);

const TabletGuide: React.FC = () => (
  <div>
    <h2 style={S.h2}>Tablet Mode</h2>
    <p style={S.p}>
      Tablet Mode is auto-detected on touch devices and can also be toggled manually
      via <strong>View → Tablet Mode</strong>. It replaces mouse-centric controls with
      touch-friendly button panels.
    </p>

    <h3 style={S.h3}>Camera row</h3>
    <p style={S.p}>
      Appears inside the camera panel (top-right area of the viewport). Three buttons
      select what a finger-drag on the sphere background does:
    </p>
    <div style={S.grid}>
      <span style={S.label}>⤾ Orbit</span>
      <span style={S.value}>Drag to rotate the camera around the sphere.</span>
      <span style={S.label}>⊕ Zoom</span>
      <span style={S.value}>Drag up/down to zoom in or out.</span>
      <span style={S.label}>✛ Pan</span>
      <span style={S.value}>Drag to pan the camera laterally.</span>
    </div>

    <h3 style={S.h3}>Sphere row</h3>
    <p style={S.p}>
      Floats at the bottom-right of the viewport. Controls what touching the sphere does:
    </p>
    <div style={S.grid}>
      <span style={S.label}>✏ Draw</span>
      <span style={S.value}>Touch and drag to paint (uses the active tool).</span>
      <span style={S.label}>↻ Rotate Layer</span>
      <span style={S.value}>Touch and drag to rotate the selected layer.</span>
      <span style={S.label}>✥ Translate Layer</span>
      <span style={S.value}>Touch and drag to translate the selected layer.</span>
    </div>

    <h3 style={S.h3}>Panel toggles</h3>
    <p style={S.p}>
      The ◂ / ▸ button on the right edge collapses the right panel. The ▴ / ▾ button
      at the bottom toggles the timeline — giving you a full-screen drawing surface.
    </p>
  </div>
);

// ── Modal ─────────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'overview',  label: 'Overview',      children: <Overview /> },
  { key: 'tools',     label: 'Tools',          children: <ToolsGuide /> },
  { key: 'camera',    label: 'Camera',         children: <CameraGuide /> },
  { key: 'layers',    label: 'Layers',         children: <LayersGuide /> },
  { key: 'timeline',  label: 'Timeline',       children: <TimelineGuide /> },
  { key: 'uv',        label: 'UV & Paste',     children: <UVGuide /> },
  { key: 'projects',  label: 'Projects',       children: <ProjectGuide /> },
  { key: 'tablet',    label: 'Tablet',         children: <TabletGuide /> },
];

const UserGuideModal: React.FC<Props> = ({ open, onClose }) => (
  <Modal
    open={open}
    onCancel={onClose}
    footer={null}
    width={680}
    title={
      <span style={{ color: '#e2e8f0', fontWeight: 700, fontSize: 15, letterSpacing: 0.3 }}>
        StoryBall User Guide
      </span>
    }
    styles={{ body: { maxHeight: '72vh', overflowY: 'auto', padding: '12px 20px 20px' } }}
  >
    <Tabs
      size="small"
      items={TABS}
      tabBarStyle={{ marginBottom: 16 }}
    />
  </Modal>
);

export default UserGuideModal;
